import React from 'react';

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

/**
 * Renders a plain text string with `[BR]` markers replaced by `<br />` elements.
 * Use in Prismic Text fields where rich text is not available.
 *
 * @example
 * // In Prismic: "AI Is Easy to Deploy.[BR]Hard to Scale Safely."
 * renderTextWithLineBreaks(text)
 */
export const renderTextWithLineBreaks = (text: string) => {
  const parts = text.split('[BR]');
  return React.createElement(
    React.Fragment,
    null,
    ...parts.map((part, index) =>
      React.createElement(
        'span',
        { key: index },
        part,
        index < parts.length - 1 ? React.createElement('br') : null,
      ),
    ),
  );
};

/**
 * Renders a plain text string with `[highlight]...[/highlight]` markers
 * replaced by teal-colored `<span>` elements.
 *
 * @example
 * // In Prismic: "Here are [highlight]5 sample questions[/highlight] from our assessment"
 * renderTextWithHighlight(text)
 */
export const renderTextWithHighlight = (text: string) => {
  const regex = /\[highlight\](.*?)\[\/highlight\]/g;
  const parts: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      React.createElement(
        'span',
        { key: match.index, className: 'text-[#03BF9E]' },
        match[1],
      ),
    );
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return React.createElement(React.Fragment, null, ...parts);
};

export function cleanString(string: string): string {
  return string.replace(/[-_/]/g, ' ');
}

export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const invalidEmailDomains = ['gmail', 'hotmail', 'yahoo', 'outlook'];

export const isCorporateEmail = (email: string) => {
  const domain = email.split('@')[1];
  return !invalidEmailDomains.some((invalidDomain) =>
    domain.includes(invalidDomain),
  );
};

/**
 * Strips HTML tags and decodes the common entities HubSpot returns in rich-text
 * field labels (e.g. `<strong>`, `<em>`, `<br>`, `&nbsp;`), leaving plain text.
 * Returns '' for nullish input.
 */
export const htmlToPlainText = (raw?: string | null): string =>
  (raw ?? '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&#xa0;|&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
